/**
 * Card-level translation.
 *
 * A single approval card frequently surfaces several foreign-language fields at
 * once - an App Store review plus its proposed public reply, or an inbound
 * customer email plus the proposed response. Rather than give each field its
 * own translate control, the whole card shares ONE control: the operator clicks
 * "Translate to English" once and every registered field flips together, and
 * can flip back just as easily.
 *
 * How it fits together:
 *
 *   - `CardTranslationProvider` wraps a card and owns the shared state (which
 *     fields are translatable, their translations, whether we're currently
 *     showing the translation, the in-flight status).
 *   - `useCardTranslatedText(text)` is called by each field renderer. It
 *     registers the field's (plain-text) content for translation and returns
 *     the text that should currently be shown - the original, or the
 *     translation when the card is flipped to English.
 *   - `CardTranslateControl` renders the single toggle. It only appears when at
 *     least one field on the card looks translatable.
 *
 * Flipping the card to English translates every registered field in ONE batch
 * request (see `translateTexts`). Fields that mount later (e.g. when the
 * operator expands a collapsed section while the card is already flipped) are
 * picked up automatically and translated on the next tick.
 *
 * We only ever translate PLAIN TEXT. Callers that hold HTML (the inbound email
 * body) are responsible for handing us a plain-text representation; HTML is
 * never sent to the translate endpoint.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Languages, Loader2, AlertTriangle, RotateCcw } from 'lucide-react'

import {
  translateTexts,
  looksTranslatable,
  languageDisplayName,
  type TranslationStatus,
} from '@/features/queue/translation'

interface CardTranslationValue {
  /** Register a field's original (plain-text) content as translatable. */
  registerText: (text: string) => void
  /** Drop a field's content when it unmounts. */
  unregisterText: (text: string) => void
  /** The translation for a given original text, if we have it yet. */
  translationFor: (text: string) => string | undefined
  /** Whether the card is currently showing translations (vs. originals). */
  showingTranslation: boolean
  /** In-flight translation status for the card as a whole. */
  status: TranslationStatus
  /** True once at least one registered field looks translatable. */
  hasTranslatable: boolean
  /** A detected source-language code to surface (e.g. "ja"); may be empty. */
  detectedLanguage: string
  /** Flip between showing originals and translations. */
  toggle: () => void
  /** Retry after a failed translation. */
  retry: () => void
  /**
   * Abandon translation and return to the original view. Provides a clean
   * escape from the error state: the operator is never stuck on "translation
   * failed" with no way forward — they can dismiss and read the originals.
   */
  dismiss: () => void
}

const CardTranslationContext = React.createContext<CardTranslationValue | null>(null)

export function CardTranslationProvider({ children }: { children: React.ReactNode }) {
  // Registered original texts (deduped). Fields add/remove themselves as they
  // mount and unmount.
  const [registered, setRegistered] = React.useState<string[]>([])
  // original text -> translated text.
  const [translations, setTranslations] = React.useState<Map<string, string>>(new Map())
  const [status, setStatus] = React.useState<TranslationStatus>('idle')
  const [showingTranslation, setShowingTranslation] = React.useState(false)
  const [detectedLanguage, setDetectedLanguage] = React.useState('')
  // Bumped by retry() to re-run the translation effect after a failure.
  const [retryNonce, setRetryNonce] = React.useState(0)

  const registerText = React.useCallback((text: string) => {
    setRegistered((prev) => (prev.includes(text) ? prev : [...prev, text]))
  }, [])

  const unregisterText = React.useCallback((text: string) => {
    setRegistered((prev) => (prev.includes(text) ? prev.filter((t) => t !== text) : prev))
  }, [])

  const translationFor = React.useCallback(
    (text: string) => translations.get(text),
    [translations],
  )

  const toggle = React.useCallback(() => {
    setShowingTranslation((v) => !v)
  }, [])

  const retry = React.useCallback(() => {
    setStatus('idle')
    setRetryNonce((n) => n + 1)
  }, [])

  const dismiss = React.useCallback(() => {
    setShowingTranslation(false)
    setStatus('idle')
  }, [])

  // While the card is flipped to English, translate any registered field we
  // don't have yet. This fires on the initial flip and again whenever a new
  // translatable field mounts (e.g. an expanded section), so late arrivals are
  // translated without the operator having to toggle again.
  //
  // Two safeguards against request churn:
  //   - DEBOUNCE: a short timer collapses a burst of late-mounting fields
  //     (e.g. expanding several collapsed sections at once) into ONE batch
  //     request instead of firing a request per registration.
  //   - ABORT: an AbortController cancels the in-flight request if the effect
  //     re-runs or the card unmounts, so we never burn translate quota on a
  //     result that will be discarded.
  React.useEffect(() => {
    if (!showingTranslation) return
    const missing = registered.filter((t) => !translations.has(t))
    if (missing.length === 0) return

    // Reflect the pending work immediately so the control shows "Translating…"
    // without waiting out the debounce window.
    setStatus('loading')

    const controller = new AbortController()
    const handle = setTimeout(() => {
      translateTexts(missing, { signal: controller.signal })
        .then((results) => {
          if (controller.signal.aborted) return
          setTranslations((prev) => {
            const next = new Map(prev)
            results.forEach((r, i) => next.set(missing[i], r.translatedText))
            return next
          })
          setDetectedLanguage(
            (prev) => prev || results.find((r) => r.detectedSourceLanguage)?.detectedSourceLanguage || '',
          )
          setStatus('done')
        })
        .catch(() => {
          // Aborts are expected churn, not failures — only surface real errors.
          if (!controller.signal.aborted) setStatus('error')
        })
    }, 150)

    return () => {
      clearTimeout(handle)
      controller.abort()
    }
  }, [showingTranslation, registered, translations, retryNonce])

  const hasTranslatable = registered.length > 0

  const value = React.useMemo<CardTranslationValue>(
    () => ({
      registerText,
      unregisterText,
      translationFor,
      showingTranslation,
      status,
      hasTranslatable,
      detectedLanguage,
      toggle,
      retry,
      dismiss,
    }),
    [
      registerText,
      unregisterText,
      translationFor,
      showingTranslation,
      status,
      hasTranslatable,
      detectedLanguage,
      toggle,
      retry,
      dismiss,
    ],
  )

  return <CardTranslationContext.Provider value={value}>{children}</CardTranslationContext.Provider>
}

/**
 * Field hook. Registers `text` as a translatable field on the surrounding card
 * and returns the text that should currently be shown - the original, or the
 * translation when the card is flipped to English. Non-translatable text (plain
 * English, symbol-only fields like star ratings) is never registered and is
 * returned unchanged. Safe to call with no provider (returns the original).
 */
export function useCardTranslatedText(text: string): string {
  const ctx = React.useContext(CardTranslationContext)
  const register = ctx?.registerText
  const unregister = ctx?.unregisterText
  const translatable = !!text && looksTranslatable(text)

  React.useEffect(() => {
    if (!register || !unregister || !translatable) return
    register(text)
    return () => unregister(text)
    // register / unregister are stable (useCallback []), so this runs once per
    // distinct text and cleans up on unmount.
  }, [register, unregister, translatable, text])

  if (ctx && translatable && ctx.showingTranslation) {
    return ctx.translationFor(text) ?? text
  }
  return text
}

/**
 * Whether the surrounding card is currently showing translations. Lets a
 * renderer choose a different layout for the translated view - e.g. the inbound
 * email renderer swaps its sandboxed HTML frame for a translated plain-text
 * block. Returns false when there is no provider.
 */
export function useCardShowingTranslation(): boolean {
  const ctx = React.useContext(CardTranslationContext)
  return ctx?.showingTranslation ?? false
}

/**
 * The single card-level translate toggle. Renders nothing until at least one
 * field on the card looks translatable.
 */
export function CardTranslateControl() {
  const ctx = React.useContext(CardTranslationContext)
  if (!ctx || !ctx.hasTranslatable) return null

  const { status, showingTranslation, toggle, retry, dismiss, detectedLanguage } = ctx

  if (showingTranslation && status === 'loading') {
    return (
      <div style={styles.row}>
        <span style={styles.statusMuted}>
          <Loader2 style={styles.icon} className="animate-spin" aria-hidden="true" />
          Translating&hellip;
        </span>
      </div>
    )
  }

  if (status === 'error') {
    // Always offer a way out: retry the translation, or dismiss back to the
    // original view. The registered fields already fall back to their original
    // text on failure, so "Show original" gives the operator a clean,
    // fully-legible card without forcing a successful translation first.
    return (
      <div style={styles.row}>
        <button type="button" style={styles.actionError} onClick={retry} title="Retry translation">
          <AlertTriangle style={styles.icon} aria-hidden="true" />
          Translation failed - retry
        </button>
        <button type="button" style={styles.action} onClick={dismiss} title="Dismiss and show the original text">
          <RotateCcw style={styles.icon} aria-hidden="true" />
          Show original
        </button>
      </div>
    )
  }

  // Never translated yet: offer to translate the card.
  if (status !== 'done') {
    return (
      <div style={styles.row}>
        <button type="button" style={styles.action} onClick={toggle} title="Translate this card to English">
          <Languages style={styles.icon} aria-hidden="true" />
          Translate to English
        </button>
      </div>
    )
  }

  // Translated at least once: offer to flip between original and English.
  const from = languageDisplayName(detectedLanguage)
  return (
    <div style={styles.row}>
      <button
        type="button"
        style={styles.action}
        onClick={toggle}
        title={showingTranslation ? 'Show the original text' : 'Show the English translation'}
      >
        <RotateCcw style={styles.icon} aria-hidden="true" />
        {showingTranslation ? 'Show original' : 'Show English'}
      </button>
      {showingTranslation && from ? <span style={styles.fromLabel}>Translated from {from}</span> : null}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--accent-secondary)',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition:
      'background-color var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast)',
  },
  actionError: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    background: 'transparent',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--error)',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  statusMuted: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--text-muted)',
    fontSize: '12px',
    fontWeight: 500,
  },
  fromLabel: {
    color: 'var(--text-muted)',
    fontSize: '11px',
  },
  icon: {
    width: '13px',
    height: '13px',
  },
}

export default CardTranslateControl
